/**
 * Utilitaires pour la gestion des statuts et couleurs
 * Centralise toute la logique de style et de libellé des statuts
 */

import { RESERVATION_STATUS, PAYMENT_STATUS } from '../constants/statusCodes';

/**
 * Palette de couleurs pour les statuts
 */
const STATUS_COLORS = {
  SUCCESS: {
    text: '#166534',
    bg: '#dcfce7',
    tailwind: 'text-green-600 bg-green-100'
  },
  WARNING: {
    text: '#92400e',
    bg: '#fef3c7',
    tailwind: 'text-yellow-600 bg-yellow-100'
  },
  INFO: {
    text: '#1e40af',
    bg: '#dbeafe',
    tailwind: 'text-blue-600 bg-blue-100'
  },
  ERROR: {
    text: '#991b1b',
    bg: '#fee2e2',
    tailwind: 'text-red-600 bg-red-100'
  },
  NEUTRAL: {
    text: '#374151',
    bg: '#f3f4f6',
    tailwind: 'text-gray-600 bg-gray-100'
  },
  PURPLE: {
    text: '#7c3aed',
    bg: '#ede9fe',
    tailwind: 'text-purple-600 bg-purple-100'
  },
  ORANGE: {
    text: '#ea580c',
    bg: '#fed7aa',
    tailwind: 'text-orange-600 bg-orange-100'
  },
  INDIGO: {
    text: '#4338ca',
    bg: '#e0e7ff',
    tailwind: 'text-indigo-600 bg-indigo-100'
  }
};

/**
 * Configuration des statuts de réservation
 */
const RESERVATION_STATUS_CONFIG = {
  [RESERVATION_STATUS.PENDING]: {
    label: 'En attente',
    color: STATUS_COLORS.WARNING,
    icon: '⏳'
  },
  [RESERVATION_STATUS.CONFIRMED]: {
    label: 'Confirmée',
    color: STATUS_COLORS.SUCCESS,
    icon: '✅'
  },
  [RESERVATION_STATUS.ACTIVE]: {
    label: 'Active',
    color: STATUS_COLORS.PURPLE,
    icon: '🚀'
  },
  [RESERVATION_STATUS.COMPLETED]: {
    label: 'Terminée',
    color: STATUS_COLORS.INFO,
    icon: '🏁'
  },
  [RESERVATION_STATUS.CANCELLED]: {
    label: 'Annulée',
    color: STATUS_COLORS.ERROR,
    icon: '❌'
  },
  [RESERVATION_STATUS.REFUNDED]: {
    label: 'Remboursée',
    color: STATUS_COLORS.INDIGO,
    icon: '💰'
  },
  [RESERVATION_STATUS.EXPIRED]: {
    label: 'Expirée',
    color: STATUS_COLORS.ORANGE,
    icon: '⏰'
  },
  [RESERVATION_STATUS.PAYMENT_FAILED]: {
    label: 'Paiement échoué',
    color: STATUS_COLORS.ERROR,
    icon: '💳'
  }
};

/**
 * Configuration des statuts de paiement
 */
const PAYMENT_STATUS_CONFIG = {
  [PAYMENT_STATUS.PENDING]: {
    label: 'En attente',
    color: STATUS_COLORS.WARNING,
    icon: '⏳'
  },
  [PAYMENT_STATUS.PROCESSING]: {
    label: 'En cours',
    color: STATUS_COLORS.WARNING,
    icon: '🔄'
  },
  [PAYMENT_STATUS.SUCCEEDED]: {
    label: 'Réussi',
    color: STATUS_COLORS.SUCCESS,
    icon: '✅'
  },
  [PAYMENT_STATUS.COMPLETED]: {
    label: 'Complété',
    color: STATUS_COLORS.SUCCESS,
    icon: '✅'
  },
  [PAYMENT_STATUS.FAILED]: {
    label: 'Échoué',
    color: STATUS_COLORS.ERROR,
    icon: '❌'
  },
  [PAYMENT_STATUS.CANCELLED]: {
    label: 'Annulé',
    color: STATUS_COLORS.ERROR,
    icon: '🚫'
  },
  [PAYMENT_STATUS.REFUNDED]: {
    label: 'Remboursé',
    color: STATUS_COLORS.INDIGO,
    icon: '💰'
  },
  [PAYMENT_STATUS.PARTIALLY_REFUNDED]: {
    label: 'Partiellement remboursé',
    color: STATUS_COLORS.INDIGO,
    icon: '💵'
  },
  [PAYMENT_STATUS.PARTIAL]: {
    label: 'Partiel',
    color: STATUS_COLORS.WARNING,
    icon: '⚠️'
  },
  [PAYMENT_STATUS.NO_REFUND_NEEDED]: {
    label: 'Pas de remboursement nécessaire',
    color: STATUS_COLORS.NEUTRAL,
    icon: '✓'
  }
};

// ============================================
// FONCTIONS UTILITAIRES POUR RÉSERVATIONS
// ============================================

/**
 * Obtient le libellé français d'un statut de réservation
 * @param {string} status - Statut de réservation
 * @returns {string} Libellé en français
 */
export const getReservationStatusLabel = (status) => {
  return RESERVATION_STATUS_CONFIG[status]?.label || status || 'Inconnu';
};

/**
 * Obtient l'icône d'un statut de réservation
 * @param {string} status - Statut de réservation
 * @returns {string} Emoji d'icône
 */
export const getReservationStatusIcon = (status) => {
  return RESERVATION_STATUS_CONFIG[status]?.icon || '❓';
};

/**
 * Obtient les couleurs Tailwind CSS pour un statut de réservation
 * @param {string} status - Statut de réservation
 * @returns {string} Classes Tailwind CSS
 */
export const getReservationStatusTailwindClasses = (status) => {
  return RESERVATION_STATUS_CONFIG[status]?.color?.tailwind || STATUS_COLORS.NEUTRAL.tailwind;
};

/**
 * Obtient les styles Material-UI pour un statut de réservation
 * @param {string} status - Statut de réservation
 * @returns {Object} Objet de styles Material-UI
 */
export const getReservationStatusMuiStyles = (status) => {
  const config = RESERVATION_STATUS_CONFIG[status];
  return {
    bgcolor: config?.color?.bg || STATUS_COLORS.NEUTRAL.bg,
    color: config?.color?.text || STATUS_COLORS.NEUTRAL.text
  };
};

/**
 * Vérifie si un statut de réservation est considéré comme "succès"
 * @param {string} status - Statut de réservation
 * @returns {boolean} True si c'est un statut de succès
 */
export const isReservationSuccessStatus = (status) => {
  return [RESERVATION_STATUS.CONFIRMED, RESERVATION_STATUS.COMPLETED].includes(status);
};

/**
 * Vérifie si un statut de réservation est considéré comme "actif"
 * @param {string} status - Statut de réservation
 * @returns {boolean} True si la réservation est active ou en cours
 */
export const isReservationActiveStatus = (status) => {
  return [RESERVATION_STATUS.CONFIRMED, RESERVATION_STATUS.ACTIVE].includes(status);
};

/**
 * Vérifie si un statut de réservation permet les modifications
 * @param {string} status - Statut de réservation
 * @returns {boolean} True si la réservation peut être modifiée
 */
export const canModifyReservation = (status) => {
  return [RESERVATION_STATUS.PENDING, RESERVATION_STATUS.CONFIRMED].includes(status);
};

/**
 * Vérifie si un statut de réservation permet l'annulation
 * @param {string} status - Statut de réservation
 * @returns {boolean} True si la réservation peut être annulée
 */
export const canCancelReservation = (status) => {
  return [RESERVATION_STATUS.PENDING, RESERVATION_STATUS.CONFIRMED].includes(status);
};

// ============================================
// FONCTIONS UTILITAIRES POUR PAIEMENTS
// ============================================

/**
 * Obtient le libellé français d'un statut de paiement
 * @param {string} status - Statut de paiement
 * @returns {string} Libellé en français
 */
export const getPaymentStatusLabel = (status) => {
  return PAYMENT_STATUS_CONFIG[status]?.label || status || 'Inconnu';
};

/**
 * Obtient l'icône d'un statut de paiement
 * @param {string} status - Statut de paiement
 * @returns {string} Emoji d'icône
 */
export const getPaymentStatusIcon = (status) => {
  return PAYMENT_STATUS_CONFIG[status]?.icon || '❓';
};

/**
 * Obtient les styles Material-UI pour un statut de paiement
 * @param {string} status - Statut de paiement
 * @returns {Object} Objet de styles Material-UI
 */
export const getPaymentStatusMuiStyles = (status) => {
  const config = PAYMENT_STATUS_CONFIG[status];
  return {
    bgcolor: config?.color?.bg || STATUS_COLORS.NEUTRAL.bg,
    color: config?.color?.text || STATUS_COLORS.NEUTRAL.text
  };
};

/**
 * Vérifie si un statut de paiement est considéré comme "succès"
 * @param {string} status - Statut de paiement
 * @returns {boolean} True si c'est un statut de succès
 */
export const isPaymentSuccessStatus = (status) => {
  return [PAYMENT_STATUS.SUCCEEDED, PAYMENT_STATUS.COMPLETED].includes(status);
};

/**
 * Vérifie si un statut de paiement est considéré comme "en cours"
 * @param {string} status - Statut de paiement
 * @returns {boolean} True si le paiement est en cours
 */
export const isPaymentPendingStatus = (status) => {
  return [PAYMENT_STATUS.PENDING, PAYMENT_STATUS.PROCESSING].includes(status);
};

/**
 * Vérifie si un statut de paiement est considéré comme "échec"
 * @param {string} status - Statut de paiement
 * @returns {boolean} True si le paiement a échoué
 */
export const isPaymentFailedStatus = (status) => {
  return [PAYMENT_STATUS.FAILED, PAYMENT_STATUS.CANCELLED].includes(status);
};

// ============================================
// FONCTIONS UTILITAIRES GÉNÉRIQUES
// ============================================

/**
 * Obtient l'icône appropriée pour le statut d'une icône Material-UI CheckCircle
 * @param {string} status - Statut (réservation ou paiement)
 * @param {string} type - Type de statut ('reservation' ou 'payment')
 * @returns {string} Couleur hexadécimale pour l'icône
 */
export const getStatusIconColor = (status, type = 'reservation') => {
  const config = type === 'payment' ? PAYMENT_STATUS_CONFIG : RESERVATION_STATUS_CONFIG;
  return config[status]?.color?.text || STATUS_COLORS.NEUTRAL.text;
};

/**
 * Obtient une configuration complète pour un statut
 * @param {string} status - Statut
 * @param {string} type - Type de statut ('reservation' ou 'payment')
 * @returns {Object} Configuration complète du statut
 */
export const getStatusConfig = (status, type = 'reservation') => {
  const config = type === 'payment' ? PAYMENT_STATUS_CONFIG : RESERVATION_STATUS_CONFIG;
  return config[status] || {
    label: status || 'Inconnu',
    color: STATUS_COLORS.NEUTRAL,
    icon: '❓'
  };
};

/**
 * Formatage pour Chip Material-UI avec icône
 * @param {string} status - Statut
 * @param {string} type - Type de statut ('reservation' ou 'payment')
 * @returns {Object} Props pour composant Chip
 */
export const getStatusChipProps = (status, type = 'reservation') => {
  const config = getStatusConfig(status, type);
  return {
    label: `${config.icon} ${config.label}`,
    size: 'small',
    sx: {
      bgcolor: config.color.bg,
      color: config.color.text,
      fontWeight: 'medium'
    }
  };
};

// ============================================
// EXPORTS PAR DÉFAUT POUR COMPATIBILITÉ
// ============================================

/**
 * Fonction héritée - utilisez getReservationStatusLabel à la place
 * @deprecated
 */
export const getStatusText = getReservationStatusLabel;

/**
 * Fonction héritée - utilisez getReservationStatusTailwindClasses à la place
 * @deprecated
 */
export const getStatusColor = getReservationStatusTailwindClasses;

/**
 * Fonction héritée - utilisez getPaymentStatusMuiStyles à la place
 * @deprecated
 */
export const getPaymentStatusStyle = getPaymentStatusMuiStyles;

/**
 * Fonction héritée - utilisez isReservationSuccessStatus à la place
 * @deprecated
 */
export const isSuccessStatus = isReservationSuccessStatus;
